import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api/rawFetch'

// Shapes mirror backend/schemas/purchase_order.py (POTrackingEntry /
// POTrackingResponse). Defined locally until gen-api regen, same as
// VendorUpsertRequest in useVendorMutations.ts.
// Mirrors backend TrackingCarrier. 'Other' has no tracking-URL template, so
// the backend returns url: null for it and the row renders as plain text.
export type TrackingCarrier = 'UPS' | 'DHL' | 'FedEx' | 'USPS' | 'Other'

export interface POTrackingEntry {
  tracking_id: number | null
  carrier: string
  tracking_number: string
  // Built server-side from the carrier template; null when we have no
  // template for that carrier (Arana transporters other than UPS/DHL).
  url: string | null
  // Per-shipment ETA, 'YYYY-MM-DD'. Null when the carrier gave none.
  expected_delivery: string | null
  source: 'manual' | 'arana'
  removable: boolean
  created_date: string | null
  created_by: string | null
}

export interface POTrackingResponse {
  po_id: string
  entries: POTrackingEntry[]
}

export function usePOTracking(poId: string | null) {
  return useQuery({
    queryKey: ['po-tracking', poId] as const,
    queryFn: () =>
      apiFetch<POTrackingResponse>(
        'GET',
        `/api/purchase-orders/${encodeURIComponent(poId!)}/tracking`
      ),
    enabled: poId !== null,
  })
}

// Both mutations return the full updated list, so we seed the cache with it
// and invalidate ['pos'] — the list row's has_tracking flag drives the
// "Add Tracking" vs "View Tracking" menu label.
function useTrackingCacheSync(poId: string | null) {
  const qc = useQueryClient()
  return (data: POTrackingResponse) => {
    qc.setQueryData(['po-tracking', poId], data)
    qc.invalidateQueries({ queryKey: ['pos'] })
  }
}

export function useAddPOTracking(poId: string | null) {
  const onSuccess = useTrackingCacheSync(poId)
  return useMutation({
    mutationFn: (body: {
      carrier: TrackingCarrier
      tracking_number: string
      expected_delivery?: string | null
    }) =>
      apiFetch<POTrackingResponse>(
        'POST',
        `/api/purchase-orders/${encodeURIComponent(poId!)}/tracking`,
        { body }
      ),
    onSuccess,
  })
}

export function useDeletePOTracking(poId: string | null) {
  const onSuccess = useTrackingCacheSync(poId)
  return useMutation({
    mutationFn: (trackingId: number) =>
      apiFetch<POTrackingResponse>(
        'DELETE',
        `/api/purchase-orders/${encodeURIComponent(poId!)}/tracking/${trackingId}`
      ),
    onSuccess,
  })
}
